import React, {Fragment, ReactNode, useMemo, useState} from "react";
import {IconButton, Menu} from "@mui/material";
import {PageAction, PageActionHandler} from "./PageActionHandler";
import MenuIcon from '@mui/icons-material/Menu';


function PageActionMenuButton({open, setOpen, children}: {
    children: ReactNode,
    open: boolean,
    setOpen: (open: boolean) => void,
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (open) {
            setAnchorEl(null);
            setOpen(false);
        } else {
            setAnchorEl(event.currentTarget);
            setOpen(true);
        }
    };
    const handleClose = () => {
        setAnchorEl(null);
        setOpen(false);
    };

    return (
        <>
            <IconButton onClick={handleClick}>
                <MenuIcon/>
            </IconButton>
            <Menu
                id="page-action-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {children}
            </Menu>
        </>
    );
}

export function PageActionToolbar<Action extends string, ActionContext>({context, pageActions, actionHandler}: {
    context: ActionContext,
    pageActions: Record<Action, PageAction<ActionContext>>,
    actionHandler: PageActionHandler<Action>,
}) {

    const [menuOpen, setMenuOpen] = useState(false);
    const actions = useMemo(() => Object.keys(pageActions) as Action[], [pageActions]);

    const {menu, toolbar} = useMemo(() => {
        const menu: ReactNode[] = [];
        const toolbar: ReactNode[] = [];
        actions.forEach(action => {
            const pageAction = pageActions[action];
            if (pageAction.type === 'toolbar-item') {
                toolbar.push(
                    <Fragment key={action}>
                        {pageAction.component(context, () => actionHandler.trigger(action))}
                    </Fragment>
                );
            }
            if (pageAction.type === 'menu-item') {
                menu.push(pageAction.component(context, () => {
                    actionHandler.trigger(action);
                    setMenuOpen(false);
                }));
            }
        });

        return {menu, toolbar};
    }, [actions, context, pageActions, actionHandler, setMenuOpen]);

    return (
        <>
            {toolbar}
            {menu.length > 0 && (
                <PageActionMenuButton open={menuOpen} setOpen={setMenuOpen}>{menu}</PageActionMenuButton>
            )}
        </>
    );
}
